import { Directive, Input, ElementRef, Renderer2, OnDestroy } from '@angular/core';
import { inject } from '@angular/core';

@Directive({
    selector: '[libDimOn]',
    exportAs: 'libDim',
    host: {
        '[class.lib-dim]': 'enabled'
    }
})
export class LibDimOnDirective implements OnDestroy {
    private readonly el = inject(ElementRef);
    private readonly renderer = inject(Renderer2);

    private _opacity = 0.5;

    @Input()
    set libDimOpacity(value: number) {
        this._opacity = value ?? 0.5;
        this.updateState();
    }

    @Input()
    set libDimOn(value: boolean) {
        this._enabled = coerceBooleanProperty(value);
        this.updateState();
    }

    private _enabled = false;

    private previousTransition?: string | null;

    private updateState(): void {
        const host = this.el.nativeElement as HTMLElement;
        if (!host) return;

        if (this._enabled) {
            this.previousTransition = host.style.transition || null;
            this.renderer.setStyle(host, 'transition', 'opacity 200ms ease');
            this.renderer.setStyle(host, 'opacity', `${this._opacity}`);
        } else {
            if (this.previousTransition !== null) {
                this.renderer.setStyle(host, 'transition', this.previousTransition);
            } else {
                this.renderer.removeStyle(host, 'transition');
            }
            this.renderer.removeStyle(host, 'opacity');
        }
    }

    ngOnDestroy(): void {
        const host = this.el.nativeElement as HTMLElement;
        if (host) {
            this.renderer.removeStyle(host, 'opacity');
            this.renderer.removeStyle(host, 'transition');
        }
    }

    /** Toggle the enabled state */
    public toggle(): void {
        this.libDimOn = !this._enabled;
    }

    /** Programmatically set enabled/disabled */
    public setEnabled(state: boolean): void {
        this.libDimOn = state;
    }

    /** Read-only accessor */
    public get enabled(): boolean {
        return this._enabled;
    }
}

function coerceBooleanProperty(value: any): boolean {
    return value != null && `${value}` !== 'false';
}
